/**
 * Einstellungen: Darstellung, eigenes Konto, Personen, Kategorien, Schlagwörter und
 * ein Hinweis zum Speicherort.
 */
import { useState } from 'react';
import { ApiError, api } from '../api/client.ts';
import type { Person } from '../api/types.ts';
import { useQuery } from '../api/useQuery.ts';
import { AppIcon } from '../components/AppIcon.tsx';
import { AppearancePanel } from '../components/AppearancePanel.tsx';
import { Panel } from '../components/Panel.tsx';
import { useAuth } from '../state/auth.ts';
import { useCatalog } from '../state/catalog.ts';
import { texts } from '../texts/de.ts';

/** Einstellungen der App. */
export function SettingsPage() {
  const { user, logout, changePassword } = useAuth();
  const catalog = useCatalog();
  const people = useQuery<{ users: Person[] }>('/api/users');

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  const [newCategory, setNewCategory] = useState('');
  const [newTag, setNewTag] = useState('');
  const [newPerson, setNewPerson] = useState({
    username: '',
    displayName: '',
    password: '',
    role: 'member' as 'admin' | 'member',
  });

  const isAdmin = user?.role === 'admin';

  /** Führt eine Aktion aus und meldet Fehler in Klartext. */
  async function run(action: () => Promise<void>, success: string): Promise<void> {
    setMessage(null);
    try {
      await action();
      setMessage(success);
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : texts.common.error);
    }
  }

  return (
    <section className="mx-auto max-w-3xl px-4 py-6">
      <h1 className="mb-4 text-2xl font-bold tracking-tight">{texts.settings.title}</h1>

      {message === null ? null : (
        <p className="mb-4 rounded-xl bg-accent-soft px-3 py-2 text-sm text-accent-strong">{message}</p>
      )}

      <AppearancePanel />

      <Panel title={texts.settings.account}>
        <p className="mb-3 text-sm text-content-muted">
          Angemeldet als <strong>{user?.displayName}</strong> ({user?.username}) ·{' '}
          {user?.role === 'admin' ? texts.settings.roleAdmin : texts.settings.roleMember}
        </p>
        <div className="flex flex-wrap items-end gap-3 text-sm">
          <label>
            <span className="mb-1 block font-medium">{texts.auth.currentPassword}</span>
            <input
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              className="rounded-xl border border-border-subtle bg-surface px-3 py-2"
            />
          </label>
          <label>
            <span className="mb-1 block font-medium">{texts.auth.newPassword}</span>
            <input
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              className="rounded-xl border border-border-subtle bg-surface px-3 py-2"
            />
          </label>
          <button
            type="button"
            onClick={() =>
              void run(async () => {
                await changePassword({ currentPassword, newPassword });
                setCurrentPassword('');
                setNewPassword('');
              }, 'Das Passwort wurde geändert.')
            }
            className="rounded-xl bg-accent px-4 py-2 font-semibold text-on-accent"
          >
            {texts.auth.changePassword}
          </button>
          <button
            type="button"
            onClick={() => void logout()}
            className="rounded-xl border border-border-subtle px-4 py-2"
          >
            {texts.auth.logout}
          </button>
        </div>
      </Panel>

      <Panel title={texts.settings.people}>
        {isAdmin ? null : <p className="mb-3 text-sm text-content-muted">{texts.settings.onlyAdmin}</p>}

        <ul className="mb-3 divide-y divide-border-subtle text-sm">
          {(people.data?.users ?? []).map((person) => (
            <li key={person.id} className="flex flex-wrap items-center gap-2 py-2">
              <span className="flex-1">
                <strong>{person.displayName}</strong>{' '}
                <span className="text-content-muted">({person.username})</span>
                {person.mustChangePassword ? (
                  <span className="ml-2 rounded-full bg-surface-sunken px-2 py-0.5 text-xs">
                    Startpasswort offen
                  </span>
                ) : null}
              </span>
              <select
                value={person.role}
                disabled={!isAdmin || person.id === user?.id}
                onChange={(event) =>
                  void run(async () => {
                    await api.put(`/api/users/${person.id}`, { role: event.target.value });
                    people.reload();
                  }, 'Die Rolle wurde geändert.')
                }
                className="rounded-lg border border-border-subtle bg-surface px-2 py-1 text-xs"
              >
                <option value="member">{texts.settings.roleMember}</option>
                <option value="admin">{texts.settings.roleAdmin}</option>
              </select>
              <button
                type="button"
                disabled={!isAdmin}
                onClick={() => {
                  const password = window.prompt('Neues Passwort für diese Person:');
                  if (password === null || password === '') {
                    return;
                  }
                  void run(async () => {
                    await api.post(`/api/users/${person.id}/password`, { password });
                    people.reload();
                  }, 'Das Passwort wurde zurückgesetzt.');
                }}
                className="rounded-lg border border-border-subtle px-2 py-1 text-xs disabled:opacity-40"
              >
                {texts.settings.resetPassword}
              </button>
            </li>
          ))}
        </ul>

        {isAdmin ? (
          <div className="flex flex-wrap items-end gap-3 text-sm">
            <label>
              <span className="mb-1 block font-medium">{texts.auth.username}</span>
              <input
                value={newPerson.username}
                onChange={(event) => setNewPerson({ ...newPerson, username: event.target.value })}
                className="rounded-xl border border-border-subtle bg-surface px-3 py-2"
              />
            </label>
            <label>
              <span className="mb-1 block font-medium">{texts.settings.displayName}</span>
              <input
                value={newPerson.displayName}
                onChange={(event) => setNewPerson({ ...newPerson, displayName: event.target.value })}
                className="rounded-xl border border-border-subtle bg-surface px-3 py-2"
              />
            </label>
            <label>
              <span className="mb-1 block font-medium">{texts.settings.startPassword}</span>
              <input
                type="password"
                value={newPerson.password}
                onChange={(event) => setNewPerson({ ...newPerson, password: event.target.value })}
                className="rounded-xl border border-border-subtle bg-surface px-3 py-2"
              />
            </label>
            <label>
              <span className="mb-1 block font-medium">{texts.settings.role}</span>
              <select
                value={newPerson.role}
                onChange={(event) =>
                  setNewPerson({
                    ...newPerson,
                    role: event.target.value === 'admin' ? 'admin' : 'member',
                  })
                }
                className="rounded-xl border border-border-subtle bg-surface px-3 py-2"
              >
                <option value="member">{texts.settings.roleMember}</option>
                <option value="admin">{texts.settings.roleAdmin}</option>
              </select>
            </label>
            <button
              type="button"
              onClick={() =>
                void run(async () => {
                  await api.post('/api/users', newPerson);
                  setNewPerson({ username: '', displayName: '', password: '', role: 'member' });
                  people.reload();
                }, 'Die Person wurde angelegt.')
              }
              className="inline-flex items-center gap-2 rounded-xl bg-accent px-4 py-2 font-semibold text-on-accent"
            >
              <AppIcon name="plus" size={16} />
              {texts.settings.addPerson}
            </button>
          </div>
        ) : null}
      </Panel>

      <Panel title={texts.settings.categories}>
        {isAdmin ? null : <p className="mb-3 text-sm text-content-muted">{texts.settings.onlyAdmin}</p>}
        <ul className="mb-3 flex flex-wrap gap-2 text-sm">
          {catalog.categories.map((category) => (
            <li
              key={category.id}
              className="inline-flex items-center gap-2 rounded-full bg-surface-sunken px-3 py-1"
            >
              {category.name}
              <span className="text-xs text-content-muted">{category.recipeCount}</span>
              {isAdmin ? (
                <button
                  type="button"
                  aria-label={`${category.name} löschen`}
                  onClick={() =>
                    void run(async () => {
                      await api.delete(`/api/categories/${category.id}`);
                      catalog.reload();
                    }, 'Die Kategorie wurde gelöscht.')
                  }
                  className="text-content-muted"
                >
                  ×
                </button>
              ) : null}
            </li>
          ))}
        </ul>
        {isAdmin ? (
          <div className="flex gap-2 text-sm">
            <input
              value={newCategory}
              onChange={(event) => setNewCategory(event.target.value)}
              placeholder={texts.settings.newCategory}
              className="flex-1 rounded-xl border border-border-subtle bg-surface px-3 py-2"
            />
            <button
              type="button"
              onClick={() =>
                void run(async () => {
                  await api.post('/api/categories', { name: newCategory });
                  setNewCategory('');
                  catalog.reload();
                }, 'Die Kategorie wurde angelegt.')
              }
              className="rounded-xl bg-accent px-4 py-2 font-semibold text-on-accent"
            >
              {texts.common.save}
            </button>
          </div>
        ) : null}
      </Panel>

      <Panel title={texts.settings.tags}>
        {isAdmin ? null : <p className="mb-3 text-sm text-content-muted">{texts.settings.onlyAdmin}</p>}
        <ul className="mb-3 flex flex-wrap gap-2 text-sm">
          {catalog.tags.map((tag) => (
            <li
              key={tag.id}
              className="inline-flex items-center gap-2 rounded-full bg-surface-sunken px-3 py-1"
            >
              {tag.name}
              <span className="text-xs text-content-muted">{tag.recipeCount}</span>
              {isAdmin ? (
                <button
                  type="button"
                  aria-label={`${tag.name} löschen`}
                  onClick={() =>
                    void run(async () => {
                      await api.delete(`/api/tags/${tag.id}`);
                      catalog.reload();
                    }, 'Das Schlagwort wurde gelöscht.')
                  }
                  className="text-content-muted"
                >
                  ×
                </button>
              ) : null}
            </li>
          ))}
        </ul>
        {isAdmin ? (
          <div className="flex gap-2 text-sm">
            <input
              value={newTag}
              onChange={(event) => setNewTag(event.target.value)}
              placeholder={texts.settings.newTag}
              className="flex-1 rounded-xl border border-border-subtle bg-surface px-3 py-2"
            />
            <button
              type="button"
              onClick={() =>
                void run(async () => {
                  await api.post('/api/tags', { name: newTag });
                  setNewTag('');
                  catalog.reload();
                }, 'Das Schlagwort wurde angelegt.')
              }
              className="rounded-xl bg-accent px-4 py-2 font-semibold text-on-accent"
            >
              {texts.common.save}
            </button>
          </div>
        ) : null}
      </Panel>

      <Panel title={texts.settings.storage}>
        <p className="text-sm text-content-muted">{texts.settings.storageHint}</p>
      </Panel>
    </section>
  );
}
